import { useState } from "react";
import { PRODUCT } from "@authoxi/ui-shared";
import { SessionError, signIn, type SessionInfo } from "../session";

/**
 * Sign in. The one moment the tenant key exists in this tab.
 *
 * It is posted to `/v1/bff/session`, which validates it, keeps it server-side, and returns an
 * `HttpOnly` cookie. **It is never written to storage and never held in a variable that outlives
 * this component.** The previous version put it in `localStorage`, where any script on the
 * origin could read it — an XSS, a compromised dependency, an extension — with no expiry and no
 * revocation short of minting a new tenant.
 *
 * The copy changed with it. It used to promise "stored in this browser only", which was true and
 * was the problem; saying the browser keeps nothing is both more accurate and the better pitch
 * from a company selling exactly this property.
 */
export function KeyGate({ onSignedIn }: { onSignedIn: (s: SessionInfo) => void }) {
  const [v, setV] = useState("");
  const [who, setWho] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const bad = v.length > 0 && !v.startsWith("sk_");

  async function connect() {
    if (!v || busy) return;
    setBusy(true);
    setErr(null);
    try {
      onSignedIn(await signIn(v, who));
      // Not cleared on success: the component unmounts. Cleared on failure below, so a mistyped
      // key does not sit in a form field for the rest of the session.
    } catch (e) {
      setV("");
      setErr(
        e instanceof SessionError && e.status === 429
          ? "Too many attempts. Try again in a minute."
          : e instanceof SessionError
            ? e.message
            : "That did not go through. Try again?",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gate">
      <div className="gate-card">
        <h1>authoxi console</h1>
        <p>
          Paste your tenant <b>secret key</b> to connect. It is sent once, held by the server for
          this session, and <b>never stored in this browser</b>.
        </p>
        <input
          className="input mono"
          data-testid="key-input"
          placeholder="sk_live_…"
          value={v}
          autoFocus
          disabled={busy}
          onChange={(e) => setV(e.target.value.trim())}
          onKeyDown={(e) => e.key === "Enter" && void connect()}
        />
        {bad && <div className="err">That does not look like a secret key — it starts with sk_.</div>}
        {/* Attribution, not authentication — the key above already proved the tenant. Without
            it every caller is a `viewer` the moment the tenant has members, so the team screen
            refuses its own owner; and every change lands on the admin log as "unattributed
            (tenant key)", which is honest and useless.

            It is recorded WITH the session now rather than sent as a header on each call. A
            header the browser sets is a claim the browser makes about itself. */}
        <input
          className="input"
          type="email"
          data-testid="actor-input"
          placeholder="you@company.com — who is using this key"
          value={who}
          disabled={busy}
          onChange={(e) => setWho(e.target.value.trim())}
          onKeyDown={(e) => e.key === "Enter" && void connect()}
        />
        <button
          className="btn btn-solid btn-lg"
          data-testid="key-submit"
          disabled={!v || busy}
          onClick={() => void connect()}
        >
          {busy ? "Connecting…" : "Connect"}
        </button>
        {/* Visible, and it has to be. The failure this replaces was silent: a bad key simply
            rendered a console whose every call then failed with its own separate error. */}
        {err && (
          <div className="err" role="alert" data-testid="key-error">
            {err}
          </div>
        )}
        <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
          No key yet? <a href={`${PRODUCT}/start`}>Get one</a> — no account, no card.
        </p>
      </div>
    </div>
  );
}
